import {
    ArrowDownToLine,
    ArrowUpFromLine,
    Minus,
    Percent,
    Plus,
    Loader2,
    RotateCcw,
    Settings2,
} from 'lucide-react';
import React, { lazy, Suspense, useState } from 'react';

import LegalLinks from '@/components/LegalLinks';
import { Button } from '@/components/ui/button';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import {
    InsufficientCustodialBanner,
    LegacyWalletWithdraw,
    ZeroCustodialPrompt,
    isSnappyOnboarding,
    useCustodialPanelState,
    useSnappyWalletState,
} from '@/features/custodial';
// the store module only: the flow barrel also carries the flow's dialogs.
import { useFlowStore } from '@/features/flow/store';
import { cn } from '@/lib/utils';

import { useHasUnmanagedActiveTrades } from '../hooks/useActiveUserTrades';
import { useExecutionDisplay } from '../hooks/useExecutionDisplay';
import { useRestingOrder } from '../hooks/useRestingOrder';
import { useLivePnL } from '../hooks/useLivePnL';
import type { PriceConfig } from '../hooks/usePriceConfig';
import {
    selectActiveTradeVisual,
    selectCanAfford,
    selectCustodialShortfall,
    selectEffectiveMaxCollateral,
    selectHasCustodialWarning,
    selectHasRealOpenTrade,
    selectEffectiveMinCollateral,
    selectIsRealMode,
    selectIsTradeDisabled,
    selectLeverageConstraintMessage,
    selectShowZeroCustodialPrompt,
    selectTradeDisabledReason,
    useTradingStore,
} from '../store';
import type { PositionType } from '../types';

import ActivateHip3Banner from './ActivateHip3Banner';
import ChainLoadIndicator from './ChainLoadIndicator';
import MobilePositionsSheet from './MobilePositionsSheet';
import { PercentageGrid } from './PercentageGrid';
import PositionDetails from './PositionDetails';
import PositionMarginEditor from './PositionMarginEditor';
import PositionTriggerEditor from './PositionTriggerEditor';
import { parseLimitPriceInput } from './LimitOrderControls';
import ReactivateAgentBanner from './ReactivateAgentBanner';
import RestingOrderCard from './RestingOrderCard';
import RestoreTriggersIndicator from './RestoreTriggersIndicator';
import SnappyOnboardingBanner from './SnappyOnboardingBanner';
import TradeConfigToggles from './TradeConfigToggles';
import TradePreview from './TradePreview';
import V2ShortfallPrompt from './V2ShortfallPrompt';

// The browser wallet flow's (V2) account banner, in the place the V1 banners take (see SpeedTradeRail). Lazy: a V1
// session never downloads the funds module.
const V2FundsBanner = lazy(
    async () => import('@/features/browserWallet/funds/V2FundsBanner'),
);

// Mirrors the store's INITIAL_DEMO_BALANCE (module-private there), which is
// what a demo-mode reset restores.
const DEMO_START_BALANCE = 100;

interface MobileControlPanelProps {
    currentValue: number;
    priceConfig: PriceConfig;
    onOpenPosition: (type: PositionType, amount: number) => void;
    onClosePosition: () => void;
    /** Drops the charted market's resting limit order. */
    onCancelLimitOrder: () => void;
    /** Takes the charted market's resting limit order at market. */
    onFillLimitOrder: () => void;
    isIsolatedOnly: boolean;
}

const MobileControlPanel: React.FC<MobileControlPanelProps> = ({
    currentValue,
    priceConfig,
    onOpenPosition,
    onClosePosition,
    onCancelLimitOrder,
    onFillLimitOrder,
    isIsolatedOnly,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false);

    // ── Store reads ──────────────────────────────────────────────────────
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const betAmount = useTradingStore((s) => s.betAmount);
    // Drives the always-visible balance row, the inline-stepper amount
    // input and the PnL-adaptive close button (mirrors the desktop rail).
    const balance = useTradingStore((s) => s.balance);
    // see SpeedTradeRail: total vs the part already on HyperCore that an open can spend.
    const custodialAvailable = useTradingStore((s) => s.custodialAvailable);
    const isCustodialBalanceLoadingForHint = useTradingStore(
        (s) => s.isCustodialBalanceLoading,
    );
    const isRealMode = useTradingStore(selectIsRealMode);
    // see SpeedTradeRail: the V1 account banners stay off in the browser
    // wallet flow (V2), whose own banner takes their place.
    const isBrowserWalletFlow = useFlowStore((s) => s.active === 'v2');
    // V1 only: "the rest" is USDC still on HyperEVM, which V2 does not have.
    const showsTradeableHint =
        isRealMode &&
        !isBrowserWalletFlow &&
        !isCustodialBalanceLoadingForHint &&
        balance - custodialAvailable >= 0.01;
    const minCollateralUsd = useTradingStore(selectEffectiveMinCollateral);
    const balanceBelowMin =
        minCollateralUsd !== null && balance < minCollateralUsd;
    // Same three custodial flags the desktop rail reads: in real mode the
    // balance is not authoritative for the first ~10-15s, so a bare "$0.00"
    // here would lie to mobile users.
    const isCustodialBalanceLoading = useTradingStore(
        (s) => s.isCustodialBalanceLoading,
    );
    const isCustodialBalanceSettling = useTradingStore(
        (s) => s.isCustodialBalanceSettling,
    );
    const isCustodialBalanceProvisional = useTradingStore(
        (s) => s.isCustodialBalanceProvisional,
    );
    const adjustBet = useTradingStore((s) => s.adjustBet);
    const setDemoBalance = useTradingStore((s) => s.setDemoBalance);
    const setDirectBetAmount = useTradingStore((s) => s.setDirectBetAmount);
    // Caps the "+" stepper (the store clamps regardless — this is the
    // affordance that says so before the click).
    const effectiveMaxCollateral = useTradingStore(
        selectEffectiveMaxCollateral,
    );
    const { livePnLValue, livePnLPercent } = useLivePnL(currentValue);
    const orderType = useTradingStore((s) => s.orderType);
    const limitPriceDraft = useTradingStore((s) => s.limitPrice);
    // the charted market's resting limit order, real or demo.
    const restingOrder = useRestingOrder();

    const canAffordStore = useTradingStore(selectCanAfford);
    const isTradeDisabled = useTradingStore(selectIsTradeDisabled);
    const hasRealOpenTrade = useTradingStore(selectHasRealOpenTrade);
    const custodialShortfall = useTradingStore(selectCustodialShortfall);
    const hasCustodialWarning = useTradingStore(selectHasCustodialWarning);
    const zeroCustodialPromptEligible = useTradingStore(
        selectShowZeroCustodialPrompt,
    );
    const leverageConstraintMessage = useTradingStore(
        selectLeverageConstraintMessage,
    );
    const resumeUnverified = useTradingStore((s) => s.resumeUnverified);
    const tradeDisabledReason = useTradingStore(selectTradeDisabledReason);
    const hasUnmanagedTrades = useHasUnmanagedActiveTrades();

    // while the user has no wallet yet (S2a/S2b) the onboarding prompt owns the
    // instruction; this prompt's "deposit once" copy is only correct for a wallet
    // that already exists and is empty.
    const snappy = useSnappyWalletState();
    // V2's empty account is the browser wallet banner's "Deposit USDC to start."
    const showZeroCustodialPrompt =
        zeroCustodialPromptEligible &&
        !isSnappyOnboarding(snappy) &&
        !isBrowserWalletFlow;

    // ── Shared hooks ─────────────────────────────────────────────────────
    const { executionInFlight, executionLabel } = useExecutionDisplay();

    // ── Custodial panel actions (shared zustand store) ───────────────────
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const openWithdraw = useCustodialPanelState((s) => s.openWithdraw);

    const isLimitMode = orderType === 'limit';
    const limitPriceValue = isLimitMode
        ? parseLimitPriceInput(limitPriceDraft)
        : null;
    const canAfford =
        canAffordStore &&
        !isTradeDisabled &&
        !hasUnmanagedTrades &&
        (!isLimitMode || limitPriceValue !== null);
    const showCloseView = activePosition !== null || hasRealOpenTrade;
    // The resting card replaces the FORM only: a filled order is a position,
    // and the position view always wins.
    const showRestingView = !showCloseView && restingOrder !== null;

    // More-options drawer — triggered by the cogwheel beside the amount
    // input, so the action row below stays Long/Short only.
    const moreOptionsDrawer = (
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
                <Button
                    variant="outline"
                    size="icon"
                    aria-label="More trade options"
                    className="h-12 w-12 shrink-0"
                >
                    <Settings2 size={18} />
                </Button>
            </DrawerTrigger>
            <DrawerContent>
                <DrawerHeader>
                    <DrawerTitle>Quick Actions</DrawerTitle>
                </DrawerHeader>

                <div className="flex flex-col gap-4 px-4 pb-6">
                    {/* Quick Percentage Buttons */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Percent size={12} />
                            <span className="text-[10px] font-semibold uppercase tracking-[0.08em]">
                                Quick amount
                            </span>
                        </div>
                        <PercentageGrid
                            compact
                            onSelect={() => setDrawerOpen(false)}
                        />
                    </div>

                    <TradeConfigToggles compact />

                    <TradePreview
                        currentValue={currentValue}
                        priceConfig={priceConfig}
                    />

                    {/* Ambient HyperEVM load — available when looked for,
                        off the trade surface itself. Self-hides when the
                        backend feature is off. */}
                    <ChainLoadIndicator className="self-center" />

                    <DrawerClose asChild>
                        <Button variant="outline" className="mt-2 w-full">
                            Close
                        </Button>
                    </DrawerClose>
                </div>
            </DrawerContent>
        </Drawer>
    );

    return (
        <div className="flex flex-col gap-2">
            {/* Positions + history, one tap from the trade screen. Desktop has
                the SpeedTerminal for this; mobile had five history rows buried
                in the options drawer and no way to see an off-chart position
                at all. */}
            <MobilePositionsSheet
                priceConfig={priceConfig}
                onClosePosition={onClosePosition}
                onCancelLimitOrder={onCancelLimitOrder}
                onFillLimitOrder={onFillLimitOrder}
            />

            {/* Balance stays visible above the controls, in the order form
                AND while a position is open */}
            <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/30 px-3 py-2">
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Available balance
                    </span>
                    {/* Spinner while the custodial balance is in flight,
                        dimmed pulse while it settles after a trade — the
                        number must never read as authoritative early. */}
                    <span
                        className={cn(
                            'font-mono text-lg font-semibold tabular-nums tracking-tight text-foreground',
                            isRealMode &&
                                isCustodialBalanceSettling &&
                                'animate-pulse opacity-60',
                            // read succeeded, trading authority still resolving:
                            // show the real number immediately rather than a 0 or
                            // a spinner, dimmed so it does not read as "ready to
                            // trade" yet.
                            isCustodialBalanceProvisional && 'opacity-50',
                        )}
                    >
                        {isCustodialBalanceLoading ? (
                            <Loader2
                                size={16}
                                className="inline animate-spin text-muted-foreground"
                            />
                        ) : (
                            <>
                                ${balance.toFixed(2)}{' '}
                                <span className="text-[10px] font-medium text-muted-foreground">
                                    USDC
                                </span>
                            </>
                        )}
                    </span>
                    {showsTradeableHint && (
                        // the one-button transfer lives on the wallet pane's balance header
                        <button
                            type="button"
                            onClick={() => openDeposit()}
                            className="truncate text-left text-[10px] text-primary underline-offset-2 hover:underline"
                        >
                            Tradeable now ${custodialAvailable.toFixed(2)}, tap
                            to move the rest to HyperCore
                        </button>
                    )}
                </div>
                {isRealMode ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openDeposit()}
                            className="h-7 gap-1 px-2 text-[10px] font-semibold"
                        >
                            <ArrowDownToLine size={11} />
                            Deposit
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openWithdraw()}
                            className="h-7 gap-1 px-2 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            <ArrowUpFromLine size={11} />
                            Withdraw
                        </Button>
                    </div>
                ) : (
                    // Demo has no deposit affordance, so a drained demo balance
                    // is a dead end without this (mirrors the desktop rail).
                    balanceBelowMin &&
                    !activePosition && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDemoBalance(DEMO_START_BALANCE)}
                            className="h-7 shrink-0 gap-1 px-2 text-[10px] font-semibold"
                        >
                            <RotateCcw size={11} />
                            Reset demo
                        </Button>
                    )
                )}
            </div>

            {/* The account prompts: the browser wallet's own banner in V2,
                the custodial wallet's in V1. */}
            {isBrowserWalletFlow ? (
                <Suspense fallback={null}>
                    <V2FundsBanner />
                </Suspense>
            ) : (
                <>
                    {/* Perps-wallet reactivation prompt (near the trade form) */}
                    {/* Snappy-Wallet onboarding prompt — mutually exclusive with reactivation below */}
                    <SnappyOnboardingBanner />

                    {/* Self-serve recovery of funds on a previous-CWM wallet (inert while
                        lib/config/custodialManagers.ts declares no previous manager) */}
                    <LegacyWalletWithdraw />

                    <ReactivateAgentBanner />

                    {/* One-time HIP-3 activation prompt (near the trade form) */}
                    <ActivateHip3Banner />
                </>
            )}

            {/* Ambiguous-transport TP/SL recovery indicator (near the trade
                form); both flows, through the backend seam */}
            <RestoreTriggersIndicator />

            {isIsolatedOnly && !showCloseView && (
                <p className="rounded-lg border border-sky-400/20 bg-sky-400/5 px-3 py-2 text-xs text-sky-200/80">
                    Isolated margin, opening may take a little longer while
                    leverage is configured.
                </p>
            )}

            {/* Main Controls Row */}
            {showRestingView && restingOrder !== null ? (
                <RestingOrderCard
                    order={restingOrder}
                    markPrice={currentValue}
                    formatPrice={priceConfig.formatDisplay}
                    onCancel={onCancelLimitOrder}
                    onFill={onFillLimitOrder}
                    busy={executionInFlight}
                    compact
                />
            ) : !showCloseView ? (
                showZeroCustodialPrompt ? (
                    <ZeroCustodialPrompt onFund={() => openDeposit()} />
                ) : (
                    <>
                        {/* Bet Amount Input Row — −/＋ steppers live INSIDE
                            the input, the value renders centered with its $
                            glued to the number ($0 when empty), and the
                            options cogwheel sits beside the input. */}
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <button
                                    type="button"
                                    aria-label="Decrease amount by $10"
                                    onClick={() => adjustBet(-10)}
                                    disabled={
                                        betAmount <= 0 || executionInFlight
                                    }
                                    className="absolute left-1.5 top-1/2 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg bg-secondary/70 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                                >
                                    <Minus size={14} />
                                </button>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    aria-label="Amount in USD"
                                    value={`$${betAmount === 0 ? '0' : betAmount}`}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(
                                            /[^0-9.]/g,
                                            '',
                                        );
                                        if (raw === '') {
                                            setDirectBetAmount(0);
                                            return;
                                        }
                                        const value = parseFloat(raw);
                                        if (!isNaN(value)) {
                                            setDirectBetAmount(value);
                                        }
                                    }}
                                    disabled={executionInFlight}
                                    className="h-12 w-full rounded-xl border border-border bg-input px-12 text-center font-mono text-lg font-bold tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    aria-label="Increase amount by $10"
                                    onClick={() => adjustBet(10)}
                                    disabled={
                                        (effectiveMaxCollateral !== null &&
                                            betAmount >=
                                                effectiveMaxCollateral) ||
                                        executionInFlight
                                    }
                                    className="absolute right-1.5 top-1/2 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg bg-secondary/70 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                            {moreOptionsDrawer}
                        </div>

                        {/* Custodial shortfall warning (V2: the same ask
                            without the custodial wording) */}
                        {hasCustodialWarning &&
                            custodialShortfall > 0 &&
                            (isBrowserWalletFlow ? (
                                <V2ShortfallPrompt
                                    shortfall={custodialShortfall}
                                    onDeposit={() =>
                                        openDeposit(custodialShortfall)
                                    }
                                />
                            ) : (
                                <InsufficientCustodialBanner
                                    shortfall={custodialShortfall}
                                    onDeposit={() =>
                                        openDeposit(custodialShortfall)
                                    }
                                />
                            ))}

                        {/* What the buttons will place, when that is not "now
                            at the market": the price input lives in the Quick
                            Actions drawer, so the armed price has to be visible
                            out here or the CTA is unlabelled. */}
                        {isLimitMode && (
                            <button
                                type="button"
                                onClick={() => setDrawerOpen(true)}
                                className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-2 text-left font-mono text-[10px] tabular-nums text-muted-foreground"
                            >
                                <span>Limit order</span>
                                <span className="font-bold text-foreground">
                                    {limitPriceValue !== null
                                        ? `at $${priceConfig.formatDisplay(limitPriceValue)}`
                                        : 'set a price'}
                                </span>
                            </button>
                        )}

                        {/* Action Buttons Row — gap-0: the joined pair's
                            slash gap comes from the clip-path */}
                        <div className="flex items-center gap-0">
                            <Button
                                variant="cyber-long"
                                onClick={() =>
                                    canAfford &&
                                    onOpenPosition('LONG', betAmount)
                                }
                                disabled={!canAfford || executionInFlight}
                                className="st-cta-joined h-14 flex-1 gap-2"
                            >
                                {executionInFlight ? (
                                    <>
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                        />
                                        <span className="text-xs font-semibold">
                                            {executionLabel}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-base font-black tracking-wide">
                                        Long
                                    </span>
                                )}
                            </Button>

                            <Button
                                variant="cyber-short"
                                onClick={() =>
                                    canAfford &&
                                    onOpenPosition('SHORT', betAmount)
                                }
                                disabled={!canAfford || executionInFlight}
                                className="st-cta-joined h-14 flex-1 gap-2"
                            >
                                {executionInFlight ? (
                                    <>
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                        />
                                        <span className="text-xs font-semibold">
                                            {executionLabel}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-base font-black tracking-wide">
                                        Short
                                    </span>
                                )}
                            </Button>
                        </div>

                        {/* Policy links below the CTA pair — the last thing on
                            the mobile trade surface. Chain-load telemetry moved
                            into the Quick Actions drawer, where the rest of the
                            secondary detail lives. */}
                        <LegalLinks className="self-center" />

                        {!executionInFlight &&
                            (hasUnmanagedTrades ? (
                                <div className="text-center text-[11px] text-destructive">
                                    Open trades detected, resolve them before
                                    trading
                                </div>
                            ) : resumeUnverified ? (
                                <div className="text-center text-[11px] text-muted-foreground">
                                    Syncing trade state…
                                </div>
                            ) : tradeDisabledReason === 'pairData' ? (
                                <div className="text-center text-[11px] text-destructive">
                                    Market data for this pair is unavailable
                                    right now. Trading is paused until it loads.
                                </div>
                            ) : tradeDisabledReason === 'authorityResolving' ? (
                                <div className="text-center text-[11px] text-muted-foreground">
                                    Finishing sign in, trading unlocks in a
                                    moment.
                                </div>
                            ) : tradeDisabledReason === 'reactivating' ? (
                                <div className="text-center text-[11px] text-muted-foreground">
                                    Wallet setup in progress. Trading unlocks
                                    when it finishes.
                                </div>
                            ) : isLimitMode && limitPriceValue === null ? (
                                <div className="text-center text-[11px] text-muted-foreground">
                                    Enter a limit price to place the order.
                                </div>
                            ) : (
                                (leverageConstraintMessage ||
                                    (!canAffordStore &&
                                        !hasCustodialWarning &&
                                        'Insufficient funds')) && (
                                    <div className="text-center text-[11px] text-destructive">
                                        {leverageConstraintMessage ??
                                            'Insufficient funds'}
                                    </div>
                                )
                            ))}
                    </>
                )
            ) : (
                /* Open position summary + close */
                <div className="flex flex-col gap-2">
                    <PositionDetails
                        currentValue={currentValue}
                        priceConfig={priceConfig}
                        compact
                    />
                    {/* Same post-open TP/SL editor as the desktop rail. */}
                    <PositionTriggerEditor
                        currentValue={currentValue}
                        compact
                    />
                    {/* Same add-margin editor as the desktop rail. */}
                    <PositionMarginEditor compact />
                    {/* The close button adapts to the live PnL sign (lime
                        slab in profit, pink in loss) and shows the PnL,
                        like the desktop rail. */}
                    <Button
                        onClick={onClosePosition}
                        variant={
                            livePnLValue >= 0 ? 'cyber-long' : 'cyber-short'
                        }
                        className="st-close-flat h-14 w-full gap-2"
                        size="lg"
                        disabled={executionInFlight}
                    >
                        {executionInFlight ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                <span className="font-bold">
                                    {executionLabel}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="font-bold">Close</span>
                                <span className="font-mono font-bold tabular-nums">
                                    {livePnLValue >= 0 ? '+' : ''}$
                                    {livePnLValue.toFixed(2)} (
                                    {livePnLValue >= 0 ? '+' : ''}
                                    {livePnLPercent.toFixed(2)}%)
                                </span>
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default MobileControlPanel;
